import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { StatusChip, type StatusTone } from '~/components/ui/status-chip';

interface StatusStripProps {
  chip: { label: string; tone: StatusTone };
  /** The machine identity, shown mono. */
  id?: string;
  /** When it last happened: a `LocalTime`. */
  time?: ReactNode;
  /** Supporting facts, separated by `|`. */
  facts?: ReactNode[];
  /** At most one jump link, at the right end. */
  jump?: { label: string; href: string };
  testId?: string;
}

const Divider = () => <span className="text-gray-300 dark:text-gray-700">|</span>;

/** The one status strip for a live entity page: chip, id, time, facts and one jump link; it reports and never acts (DESIGN.md §1.2). */
export function StatusStrip({ chip, id, time, facts = [], jump, testId }: StatusStripProps) {
  return (
    <div
      role="status"
      data-testid={testId}
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm dark:border-gray-800 dark:bg-gray-900"
    >
      <StatusChip label={chip.label} tone={chip.tone} dot />
      {id && <span className="font-mono text-gray-800 dark:text-white/90">{id}</span>}
      {time && (
        <>
          <Divider />
          <span className="text-gray-500 dark:text-gray-400">{time}</span>
        </>
      )}
      {facts.map((fact, index) => (
        <span key={index} className="contents">
          <Divider />
          <span className="text-gray-500 dark:text-gray-400">{fact}</span>
        </span>
      ))}
      {jump && (
        <Link
          to={jump.href}
          className="ml-auto rounded-sm font-medium text-brand-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:text-brand-400 dark:focus-visible:ring-offset-gray-900"
        >
          {jump.label}
        </Link>
      )}
    </div>
  );
}
